/**
 * Browser notification support for sobriety milestones.
 */

const PERMISSION_KEY = 'ssdl:notifications'

/** Check if notifications are supported */
export function notificationsSupported(): boolean {
  return 'Notification' in window
}

/** Get current permission state */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!notificationsSupported()) return 'unsupported'
  return Notification.permission
}

/** Check if user has opted in (granted + not manually disabled) */
export function notificationsEnabled(): boolean {
  if (!notificationsSupported()) return false
  if (Notification.permission !== 'granted') return false
  try {
    return localStorage.getItem(PERMISSION_KEY) !== 'disabled'
  } catch {
    return true
  }
}

/** Request permission from the browser */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!notificationsSupported()) return false
  try {
    const result = await Notification.requestPermission()
    if (result === 'granted') {
      localStorage.setItem(PERMISSION_KEY, 'enabled')
      return true
    }
    return false
  } catch {
    return false
  }
}

/** Manually disable notifications (user toggle) */
export function disableNotifications(): void {
  try {
    localStorage.setItem(PERMISSION_KEY, 'disabled')
  } catch { /* ignore */ }
}

/** Manually enable notifications (user toggle, must already have browser permission) */
export function enableNotifications(): void {
  try {
    localStorage.setItem(PERMISSION_KEY, 'enabled')
  } catch { /* ignore */ }
}

/** Send a milestone notification */
export function sendMilestoneNotification(icon: string, name: string, tierLabel: string, healthFact: string): void {
  if (!notificationsEnabled()) return

  try {
    const notification = new Notification(`${icon} ${name}`, {
      body: `${tierLabel} milestone reached! ${healthFact}`,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: `ssdl-milestone-${name}`, // prevents duplicate notifications
      requireInteraction: false,
    })

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000)

    // Focus the app when clicked
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch {
    // Notification constructor can throw in some environments
  }
}
